import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BiArrowBack, BiEnvelope, BiGlobe, BiMap, BiPhone, BiUser } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canManageMasters } from '../../../utils/permissions';
import { activeImportValues, currencyChoices, keepCurrent, loadOrganisationSettings } from '../../../utils/organisationSettings';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import './CustomerMaster.scss';

const CATEGORIES = [
  'Central authority',
  'State authority',
  'Municipal corporation',
  'Metro and rail',
  'Port authority',
  'Private client'
];

const monogram = (form) => {
  const source = String(form.shortName || form.name || '').trim();
  if (!source) return 'New';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return source.slice(0, 4).toUpperCase();
};

const placeOf = (form) => [form.city, form.state].filter(Boolean).join(', ');

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const EMPTY = {
  customerNo: '',
  name: '',
  shortName: '',
  category: 'State authority',
  pan: '',
  gstin: '',
  currency: 'INR',
  status: 'active',
  contactName: '',
  email: '',
  phone: '',
  website: '',
  portalUrl: '',
  refundFollowUpDays: '',
  refundEntitlementDays: '',
  contacts: [],
  address: '',
  city: '',
  state: 'Maharashtra',
  notes: ''
};

const CustomerFormPage = () => {
  const [currencies, setCurrencies] = useState(['INR', 'USD', 'EUR', 'GBP']);
  const [categories, setCategories] = useState(CATEGORIES);
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();
  const { customerId } = useParams();
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isCreate && !isEdit;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const revision = useMasterRevision({ entity: 'customer' });

  useDocumentTitle(isCreate ? 'New customer' : (form.name || 'Customer'));

  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const settings = data?.settings;
      if (settings?.customerCurrencies?.length) setCurrencies(settings.customerCurrencies);
      const nextCategories = activeImportValues(settings?.importLists?.customerCategories);
      if (nextCategories.length) setCategories(nextCategories);
      if (isCreate && settings?.currency) {
        setForm((current) => (current.currency === 'INR' ? { ...current, currency: settings.currency } : current));
      }
    });
  }, [isCreate]);

  useEffect(() => {
    if (isCreate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await parivartanAPI.getCustomer(customerId);
        const row = response.data?.data?.customer;
        if (!cancelled && row) {
          setForm({
            ...EMPTY,
            ...row,
            customerNo: row.customerNo || '',
            shortName: row.shortName || '',
            pan: row.pan || '',
            gstin: row.gstin || '',
            contactName: row.contactName || '',
            email: row.email || '',
            phone: row.phone || '',
            website: row.website || '',
            portalUrl: row.portalUrl || '',
            refundFollowUpDays: row.refundFollowUpDays ?? '',
            refundEntitlementDays: row.refundEntitlementDays ?? '',
            contacts: Array.isArray(row.contacts) ? row.contacts : [],
            address: row.address || '',
            city: row.city || '',
            state: row.state || '',
            notes: row.notes || ''
          });
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to load this customer');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId, isCreate]);

  const setField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (!payload.customerNo) delete payload.customerNo;
      if (!isCreate) {
        const changeReason = await revision.askReason(form.revision);
        if (!changeReason) return;
        payload.changeReason = changeReason;
      }
      if (isCreate) await parivartanAPI.createCustomer(payload);
      else await parivartanAPI.updateCustomer(customerId, payload);
      navigate('/apps/tender/masters/customers');
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save this customer');
    } finally {
      setSaving(false);
    }
  };

  if (!canManageMasters(user?.permissions, user?.roles)) {
    return <Navigate to="/apps/tender" replace />;
  }

  if (loading) {
    return (
      <div className="cust-page cust-page--loading">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  const mark = monogram(form);
  const place = placeOf(form);
  const heading = isCreate ? 'New customer' : (form.name || 'Customer');
  const locked = isView;

  return (
    <div className="cust-page">
      <form id="customer-master-form" className={`cust-record${locked ? ' cust-record--view' : ''}`} onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/masters/customers')}>
              <BiArrowBack />
              Customer
            </button>
            <div className="cust-mast__row">
              <span className={`cust-mark${mark.length > 2 ? ' cust-mark--wide' : ''}`} aria-hidden>{mark}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isCreate ? 'Customer master' : isEdit ? 'Editing customer' : 'Customer master'}</p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  {form.customerNo ? <span className="cust-chip cust-chip--no">{form.customerNo}</span> : <span className="cust-chip">Number on save</span>}
                  {form.category ? <span className="cust-chip">{form.category}</span> : null}
                  {place ? <span className="cust-chip">{place}</span> : null}
                  <span className={`cust-status cust-status--${form.status === 'active' ? 'active' : 'inactive'}`}>
                    {form.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {isView ? (
              <Button size="sm" variant="primary" onClick={() => navigate(`/apps/tender/masters/customers/${customerId}/edit`)}>
                Edit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline-primary" onClick={() => navigate('/apps/tender/masters/customers')} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={saving}>
                  {saving ? 'Saving…' : isCreate ? 'Create customer' : 'Save changes'}
                </Button>
              </>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>Identity</h2>
                  <p>The name that appears when a tender is captured.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Name" wide="8">
                  <Form.Control value={form.name} onChange={setField('name')} required disabled={locked} placeholder="National Highways Authority of India" />
                </Field>
                <Field label="Customer no." wide="4">
                  <Form.Control className="cust-input--code" value={form.customerNo} onChange={setField('customerNo')} placeholder="Assigned on save" disabled={locked} />
                </Field>
                <Field label="Short name" wide="4">
                  <Form.Control value={form.shortName} onChange={setField('shortName')} disabled={locked} placeholder="NHAI" />
                </Field>
                <Field label="Category" wide="4">
                  <Form.Select value={form.category} onChange={setField('category')} disabled={locked}>
                    {keepCurrent(categories, form.category, CATEGORIES).map((item) => <option key={item} value={item}>{item}</option>)}
                  </Form.Select>
                </Field>
                <Field label="Status" wide="4">
                  <Form.Select value={form.status} onChange={setField('status')} disabled={locked}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Form.Select>
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>02</span>
                <div>
                  <h2>Tax and currency</h2>
                  <p>Registration details and the currency of their bids.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="PAN" wide="4">
                  <Form.Control className="cust-input--code" value={form.pan} onChange={setField('pan')} disabled={locked} placeholder="AAAAA9999A" />
                </Field>
                <Field label="GSTIN" wide="4">
                  <Form.Control className="cust-input--code" value={form.gstin} onChange={setField('gstin')} disabled={locked} placeholder="27AAAAA9999A1Z5" />
                </Field>
                <Field label="Currency" wide="4">
                  <Form.Select value={form.currency} onChange={setField('currency')} disabled={locked}>
                    {currencyChoices(currencies, form.currency).map((item) => <option key={item} value={item}>{item}</option>)}
                  </Form.Select>
                </Field>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>03</span>
                <div>
                  <h2>Correspondence</h2>
                  <p>The office and the person Dhruv writes to.</p>
                </div>
              </header>
              <div className="cust-fields">
                <Field label="Contact" wide="4">
                  <Form.Control value={form.contactName} onChange={setField('contactName')} disabled={locked} placeholder="Tender cell" />
                </Field>
                <Field label="Email" wide="4">
                  <Form.Control type="email" value={form.email} onChange={setField('email')} disabled={locked} placeholder="name@authority.gov.in" />
                </Field>
                <Field label="Phone" wide="4">
                  <Form.Control value={form.phone} onChange={setField('phone')} disabled={locked} placeholder="+91" />
                </Field>
                <Field label="Website" wide="4">
                  <Form.Control value={form.website} onChange={setField('website')} disabled={locked} placeholder="https://" />
                </Field>
                <Field label="Portal URL" wide="4">
                  <Form.Control value={form.portalUrl} onChange={setField('portalUrl')} disabled={locked} placeholder="https://eprocure.example" />
                </Field>
                <Field label="Refund follow-up days" wide="4">
                  <Form.Control type="number" min="0" value={form.refundFollowUpDays} onChange={setField('refundFollowUpDays')} disabled={locked} placeholder="Reminder only. Not the refund due date." />
                </Field>
                <Field label="Refund entitlement days" wide="4">
                  <Form.Control type="number" min="0" value={form.refundEntitlementDays} onChange={setField('refundEntitlementDays')} disabled={locked} placeholder="Days after expiry. Blank if the tender states the date." />
                </Field>
                <Field label="City" wide="3">
                  <Form.Control value={form.city} onChange={setField('city')} disabled={locked} />
                </Field>
                <Field label="State" wide="3">
                  <Form.Control value={form.state} onChange={setField('state')} disabled={locked} />
                </Field>
                <Field label="Address" wide="12">
                  <Form.Control as="textarea" rows={2} value={form.address} onChange={setField('address')} disabled={locked} />
                </Field>
                <Field label="Notes" wide="12">
                  <Form.Control as="textarea" rows={3} value={form.notes} onChange={setField('notes')} disabled={locked} placeholder="Typical services, the relationship, or how Dhruv bids with them" />
                </Field>
                {(form.contacts || []).map((contact, index) => (
                  <React.Fragment key={`contact-${index}`}>
                    <Field label={index === 0 ? 'Further contact' : 'Contact'} wide="4">
                      <Form.Control value={contact.name || ''} disabled={locked} onChange={(event) => setForm((prev) => ({ ...prev, contacts: prev.contacts.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item) }))} />
                    </Field>
                    <Field label="Role" wide="4">
                      <Form.Control value={contact.role || ''} disabled={locked} onChange={(event) => setForm((prev) => ({ ...prev, contacts: prev.contacts.map((item, itemIndex) => itemIndex === index ? { ...item, role: event.target.value } : item) }))} />
                    </Field>
                    <Field label="Email" wide="4">
                      <Form.Control value={contact.email || ''} disabled={locked} onChange={(event) => setForm((prev) => ({ ...prev, contacts: prev.contacts.map((item, itemIndex) => itemIndex === index ? { ...item, email: event.target.value } : item) }))} />
                    </Field>
                  </React.Fragment>
                ))}
              </div>
              {!locked ? (
                <button type="button" className="btn btn-link px-0" onClick={() => setForm((prev) => ({ ...prev, contacts: [...(prev.contacts || []), { name: '', role: '', email: '', phone: '' }] }))}>Add a contact</button>
              ) : null}
            </section>
          </div>

          <aside className="cust-rail">
            <div className="cust-rail__card">
              <p className="cust-rail__label">At a glance</p>
              <h2>{form.shortName || form.name || 'New customer'}</h2>
              <p className="cust-rail__place">{place || 'Location not set'}</p>
              <ul>
                <li>
                  <BiUser aria-hidden />
                  <span>{form.contactName || 'No contact yet'}</span>
                </li>
                <li>
                  <BiEnvelope aria-hidden />
                  <span>{form.email || 'No email yet'}</span>
                </li>
                <li>
                  <BiPhone aria-hidden />
                  <span>{form.phone || 'No phone yet'}</span>
                </li>
                <li>
                  <BiGlobe aria-hidden />
                  {form.website ? (
                    <a
                      href={/^https?:\/\//i.test(form.website) ? form.website : `https://${form.website}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {form.website.replace(/^https?:\/\//, '')}
                    </a>
                  ) : (
                    <span>No website yet</span>
                  )}
                </li>
                <li>
                  <BiMap aria-hidden />
                  <span>{form.address || 'No address yet'}</span>
                </li>
              </ul>
            </div>
            {form.notes ? (
              <div className="cust-rail__card cust-rail__notes">
                <p className="cust-rail__label">Bidding notes</p>
                <p>{form.notes}</p>
              </div>
            ) : null}
          </aside>
        </div>
      </form>
      {revision.modals}
    </div>
  );
};

export default CustomerFormPage;
